import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import Modal from "react-responsive-modal";
import Button from "../../../../../common/button/Button";
import AFSService from "../../../../../../services/AFSService";
import AgentCheckDrilldown from "./AgentCheckDrilldown";
import WComponent from "../../../../../common/WComponent";
import "../../afs-walkthrough.css";
import "./AgentModeView.css";

// ─── Inline reclassify list ──────────────────────────────────────────────
//
// Renders one row per transaction the agent couldn't deterministically classify.
// Each row: description / amount / account picker / Reclassify button.
// Uses chartOfAccounts to populate the picker; calls onReclassify with the
// same payload shape the Review tab's GLSearchPanel uses, so reclassifications
// flow through the same backend path.
class ActionableTransactionList extends WComponent {
	state = { search: {}, selected: {}, reclassified: {} };

	render() {
		let { transactions, chartOfAccounts, onReclassify } = this.props;
		let { search, selected, reclassified } = this.state;

		let options = (chartOfAccounts || [])
			.filter(function (a) { return a.canonical_code && !String(a.canonical_code).startsWith("SUSPENSE"); })
			.map(function (a) {
				return {
					canonical_code: a.canonical_code,
					label:          a.accountName || a.display_name || a.accountDescription || a.description || a.canonical_code,
				};
			});

		function fmtAmount(n) {
			let v = parseFloat(n) || 0;
			let abs = Math.abs(v).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
			return v < 0 ? "-$" + abs : "$" + abs;
		}

		function rowKey(tx, i) {
			return tx.id != null ? "id_" + tx.id : "row_" + i + "_" + (tx.description || "");
		}

		let filteredOptions = (rk) => {
			let q = ((search[rk] || "")).trim().toLowerCase();
			if (!q) return options.slice(0, 8);
			return options.filter(function (o) {
				return o.label.toLowerCase().includes(q) || o.canonical_code.toLowerCase().includes(q);
			}).slice(0, 8);
		};

		let applyReclassify = (tx, rk) => {
			let target = selected[rk];
			if (!target || !onReclassify) return;
			onReclassify({
				transaction_id:          tx.id || null,
				transaction_description: tx.description,
				to_canonical_code:       target.canonical_code,
				scope:                   "single",
			});
			this.setState(function (prev) { return { reclassified: { ...prev.reclassified, [rk]: target.label } }; });
		};

		return (
			<div className="agent-mode__actionable-list">
				{transactions.map((tx, i) => {
					let rk = rowKey(tx, i);
					if (reclassified[rk]) {
						return (
							<div key={rk} className="agent-mode__actionable-row agent-mode__actionable-row--done">
								<Icon.CheckCircle size={14} color="#2e7d32" />
								<span className="agent-mode__actionable-desc">{tx.description}</span>
								<span className="agent-mode__actionable-amount">{fmtAmount(tx.amount)}</span>
								<span className="agent-mode__actionable-target">→ {reclassified[rk]}</span>
							</div>
						);
					}
					return (
						<div key={rk} className="agent-mode__actionable-row">
							<div className="agent-mode__actionable-tx">
								<span className="agent-mode__actionable-desc">{tx.description}</span>
								<span className="agent-mode__actionable-amount">{fmtAmount(tx.amount)}</span>
							</div>
							<div className="agent-mode__actionable-picker">
								<input
									type="text"
									className="agent-mode__actionable-search"
									placeholder="Search account…"
									value={selected[rk] ? selected[rk].label : (search[rk] || "")}
									onChange={(e) => { this.setState(function (p) { return { search: { ...p.search, [rk]: e.target.value } }; }); this.setState(function (p) { let n = { ...p.selected }; delete n[rk]; return { selected: n }; }); }}
								/>
								{(search[rk] || "").length > 0 && !selected[rk] && (
									<div className="agent-mode__actionable-options">
										{filteredOptions(rk).map((opt) => {
											return (
												<div
													key={opt.canonical_code}
													className="agent-mode__actionable-option"
													onClick={() => { this.setState(function (p) { return { selected: { ...p.selected, [rk]: opt } }; }); this.setState(function (p) { let n = { ...p.search }; delete n[rk]; return { search: n }; }); }}
												>
													{opt.label}
												</div>
											);
										})}
									</div>
								)}
							</div>
							<button
								className="agent-mode__actionable-apply"
								disabled={!selected[rk]}
								onClick={() => applyReclassify(tx, rk)}
							>
								Reclassify
							</button>
						</div>
					);
				})}
			</div>
		);
	}
}

class AgentModeView extends WComponent {
	state = {
		running: false,
		manifest: null,
		loading: true,
		savedManifest: null,
		error: null,
		liveStatus: "",
		liveSub: "",
		showInvestigation: {},
		expandedPassed: {},
		filter: null,
		showClearModal: false,
		clearing: false,
		// Live checklist during the agent run — populated from the compilation
		// event's failing_checks list, updated in place as check_started /
		// decision / check_completed events arrive. Keeps insertion order so the
		// list renders in the same order the agent walks them.
		checkProgress: [],
		iterationStats: { iteration: 0, passed: 0, failing: 0, total: 0 },
	};

	// Check for a saved manifest on mount but don't auto-display it.
	// Showing cached results without an explicit click looks identical to
	// the agent just having run — surprising for users who expect Agent Mode
	// to be opt-in. We keep the saved manifest reference so the start screen
	// can offer "View previous results" if there is one.
	componentDidMount() {
		this.loadSavedManifest();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.clientId !== this.props.clientId) {
			this.loadSavedManifest();
		}
	}

	loadSavedManifest = () => {
		AFSService.fetchAgentModeManifest({ clientId: this.props.clientId }).then((saved) => {
			if (saved) this.update({ savedManifest: saved });
			this.update({ loading: false });
		}).catch(() => {
			this.update({ loading: false });
		});
	};

	confirmClearAgentMode = async () => {
		this.update({ clearing: true });
		try {
			await AFSService.clearAgentMode({ clientId: this.props.clientId });
			this.update({ manifest: null, error: null, showClearModal: false });
			if (this.props.onComplete) this.props.onComplete(null);
		} catch (e) {
			this.update({ error: "Failed to clear: " + e.message });
		}
		this.update({ clearing: false });
	};

	startAgentMode = () => {
		this.update({
			running: true,
			manifest: null,
			error: null,
			liveStatus: "Compiling...",
			liveSub: "",
			showInvestigation: {},
			checkProgress: [],
			iterationStats: { iteration: 0, passed: 0, failing: 0, total: 0 },
		});

		AFSService.runAgentMode({
			clientId: this.props.clientId,
			onEvent: (event) => {
				if (event.type === "compilation") {
					this.update({
						liveStatus: "Iteration " + event.iteration + " — analyzing " + event.total + " checks",
						liveSub: event.passed + " passing · " + event.failing + " need attention",
						iterationStats: { iteration: event.iteration, passed: event.passed, failing: event.failing, total: event.total },
					});
					// Re-populate the checklist with this iteration's failing
					// checks. Carry over `resolved` status from the previous
					// iteration if a check is still failing this round (rare —
					// usually means the agent's fix didn't fully close it).
					this.setState(function (prev) {
						let resolvedById = {};
						for (let p of prev.checkProgress) { if (p.status === "resolved") resolvedById[p.id] = p; }
						return {
							checkProgress: (event.failing_checks || []).map(function (c) {
								return { id: c.id, label: c.label, section: c.section, status: "pending", detail: null, decision_type: null };
							}),
						};
					});
				}
				if (event.type === "check_started") {
					this.update({ liveStatus: "Investigating " + event.check_id + "..." });
					this.setState(function (prev) {
						return {
							checkProgress: prev.checkProgress.map(function (c) {
								if (c.id !== event.check_id) return c;
								let steps = (event.steps || []).map(function (s) { return { id: s.id, label: s.label, mode: s.mode || null, status: "pending", detail: null }; });
								return Object.assign({}, c, { status: "investigating", steps: steps });
							}),
						};
					});
				}
				if (event.type === "step_started") {
					this.setState(function (prev) {
						return {
							checkProgress: prev.checkProgress.map(function (c) {
								if (c.id !== event.check_id) return c;
								let steps = (c.steps || []).map(function (s) { return s.id === event.step_id ? Object.assign({}, s, { status: "investigating" }) : s; });
								return Object.assign({}, c, { steps: steps });
							}),
						};
					});
				}
				if (event.type === "step_completed") {
					this.setState(function (prev) {
						return {
							checkProgress: prev.checkProgress.map(function (c) {
								if (c.id !== event.check_id) return c;
								// Map server step status onto UI status — the
								// frontend cares about three buckets: done /
								// waiting / error. "completed" / "ready_to_propose"
								// / "completed_no_action" → done; "blocked" /
								// "waiting_for_human" → waiting; "error" → error.
								let s = event.status || "completed";
								let uiStatus = s === "blocked" || s === "waiting_for_human"
									? "waiting"
									: s === "error"
										? "error"
										: s === "not_applicable"
											? "skipped"
											: "completed";
								let steps = (c.steps || []).map(function (st) {
									return st.id === event.step_id
										? Object.assign({}, st, { status: uiStatus, detail: event.detail || null })
										: st;
								});
								return Object.assign({}, c, { steps: steps });
							}),
						};
					});
				}
				if (event.type === "investigation") {
					let conclusion = (event.evidence || []).find(function (e) { return e.step === "Conclusion"; });
					if (conclusion) this.update({ liveSub: conclusion.result.substring(0, 100) });
					// Mark as investigating in case check_started didn't fire
					this.setState(function (prev) {
						return { checkProgress: prev.checkProgress.map(function (c) { return c.id === event.check_id && c.status !== "resolved" ? Object.assign({}, c, { status: "investigating" }) : c; }) };
					});
				}
				if (event.type === "decision") {
					this.update({ liveStatus: "Resolved " + event.check_id, liveSub: event.detail || "" });
					this.setState(function (prev) {
						return { checkProgress: prev.checkProgress.map(function (c) { return c.id === event.check_id ? Object.assign({}, c, { status: "resolved", detail: event.detail || null, decision_type: event.decision_type || null }) : c; }) };
					});
				}
				if (event.type === "check_completed") {
					// If no decision fired during this check's processing,
					// mark it as unresolved so the UI shows the agent looked
					// but couldn't act. Already-resolved rows stay resolved.
					this.setState(function (prev) {
						return { checkProgress: prev.checkProgress.map(function (c) { return c.id === event.check_id && c.status !== "resolved" ? Object.assign({}, c, { status: "unresolved" }) : c; }) };
					});
				}
				if (event.type === "status") {
					this.update({ liveStatus: event.message, liveSub: "" });
				}
			},
			onComplete: (m) => {
				this.update({ manifest: m, running: false });
				if (this.props.onComplete) this.props.onComplete(m);
			},
			onError: (msg) => {
				this.update({ error: msg, running: false });
			},
		});
	};

	render() {
		let {
			onNavigateToReview,
			onNavigateToClientRequests,
			onNavigateToDocumentation,
			chartOfAccounts = [],
			onApproveReclassification = null,
		} = this.props;
		let { running, manifest, loading, savedManifest, error, liveStatus, liveSub, showInvestigation, expandedPassed, filter, showClearModal, clearing, checkProgress } = this.state;

		if (loading) {
			return (
				<div className="agent-mode">
					<div className="agent-mode__running">
						<div className="agent-mode__spinner" />
					</div>
				</div>
			);
		}

		// ── Not started ─────────────────────────────────────────────────
		if (!running && !manifest && !error) {
			let hasPrevious = !!savedManifest;
			let prevAt = hasPrevious && savedManifest.completed_at ? new Date(savedManifest.completed_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : null;
			return (
				<div className="agent-mode">
					<div className="agent-mode__start">
						<Icon.Cpu size={40} color="#d1d5db" />
						<p className="agent-mode__start-text">
							Agent Mode is opt-in. Use the standard checklist flow by default — run the agent here only when you want it to investigate and propose adjustments automatically.
						</p>
						<button className="btn btn-primary" onClick={this.startAgentMode}>
							<Icon.Play size={14} className="agent-mode__btn-icon" />
							{hasPrevious ? "Run Agent Mode again" : "Run Agent Mode"}
						</button>
						{hasPrevious && (
							<div className="agent-mode__prev-run">
								Previous run: <strong>{prevAt}</strong>
								{" — "}
								<button
									onClick={() => this.update({ manifest: savedManifest })}
									className="agent-mode__link-btn"
								>
									view previous results
								</button>
							</div>
						)}
					</div>
				</div>
			);
		}

		// ── Running ─────────────────────────────────────────────────────
		if (running) {
			let resolvedCount = checkProgress.filter(function (c) { return c.status === "resolved"; }).length;
			let typeLabel = function (t) {
				if (t === "aje_auto_accepted") return "AJE";
				if (t === "reclassification_auto_accepted") return "Reclassification";
				if (t === "client_request_created") return "Client Request";
				if (t === "acknowledgement") return "Analysis";
				if (t === "rje_auto_accepted") return "RJE";
				return null;
			};
			return (
				<div className="agent-mode">
					<div className="agent-mode__live">
						<div className="agent-mode__live-header">
							<div className="agent-mode__spinner" />
							<div className="agent-mode__live-headline">
								<div className="agent-mode__live-status">{liveStatus}</div>
								{liveSub && <div className="agent-mode__live-substatus">{liveSub}</div>}
							</div>
							{checkProgress.length > 0 && (
								<div className="agent-mode__live-progress">
									<span className="agent-mode__live-progress-count">{resolvedCount} / {checkProgress.length}</span>
									<span className="agent-mode__live-progress-label">resolved</span>
								</div>
							)}
						</div>

						{checkProgress.length > 0 && (
							<div className="agent-mode__live-checklist">
								{checkProgress.map(function (c) {
									let StatusIcon = c.status === "resolved"
										? Icon.CheckCircle
										: c.status === "investigating"
											? Icon.Loader
											: c.status === "unresolved"
												? Icon.AlertCircle
												: Icon.Circle;
									let chip = c.decision_type ? typeLabel(c.decision_type) : null;
									let showSubsteps = c.status === "investigating" && Array.isArray(c.steps) && c.steps.length > 0;
									return (
										<div key={c.id} className={clsx("agent-mode__live-check-row", "agent-mode__live-check-row--" + c.status)}>
											<div className={clsx("agent-mode__live-check", "agent-mode__live-check--" + c.status)}>
												<StatusIcon size={14} className={clsx("agent-mode__live-check-icon", c.status === "investigating" && "agent-mode__live-check-icon--spin")} />
												<span className="agent-mode__live-check-id">{c.id}</span>
												<span className="agent-mode__live-check-label">{c.label}</span>
												{chip && <span className="agent-mode__live-check-chip">{chip}</span>}
												{c.detail && c.status === "resolved" && (
													<span className="agent-mode__live-check-detail">{c.detail}</span>
												)}
											</div>
											{showSubsteps && (
												<div className="agent-mode__live-substeps">
													{c.steps.map(function (s) {
														let SubIcon = s.status === "completed"
															? Icon.Check
															: s.status === "investigating"
																? Icon.Loader
																: s.status === "waiting"
																	? Icon.Clock
																	: s.status === "skipped"
																		? Icon.Minus
																		: s.status === "error"
																			? Icon.AlertCircle
																			: Icon.Circle;
														return (
															<div key={s.id} className={clsx("agent-mode__live-substep", "agent-mode__live-substep--" + s.status)}>
																<SubIcon size={11} className={clsx("agent-mode__live-substep-icon", s.status === "investigating" && "agent-mode__live-check-icon--spin")} />
																<span className="agent-mode__live-substep-label">{s.label}</span>
																{s.detail && (s.status === "completed" || s.status === "skipped") && (
																	<span className="agent-mode__live-substep-detail">{s.detail.split("\n")[0].substring(0, 90)}</span>
																)}
															</div>
														);
													})}
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			);
		}

		// ── Error ───────────────────────────────────────────────────────
		if (error) {
			return (
				<div className="agent-mode">
					<div className="agent-mode__start">
						<Icon.AlertCircle size={40} color="#fca5a5" />
						<p className="agent-mode__start-text agent-mode__start-text--error">{error}</p>
						<button className="btn btn-secondary" onClick={this.startAgentMode}>Retry</button>
					</div>
				</div>
			);
		}

		// ── Results ─────────────────────────────────────────────────────
		if (!manifest) return null;

		let decisions = manifest.decisions || [];
		let remaining = manifest.remaining || [];
		let finalResults = manifest.final_check_results || [];
		let passedCount = finalResults.filter(function (r) { return r.passed; }).length;
		let totalCount = finalResults.length;

		return (
			<div className="agent-mode">
				<div className="agent-mode__body">

					{/* Summary — matches Review tab pattern */}
					<div className="afs-review__summary agent-mode__summary">
						{decisions.length > 0 && (
							<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", filter === "resolved" && "afs-review__summary-item--active")} onClick={() => this.update({ filter: filter === "resolved" ? null : "resolved" })}>
								<div className="afs-review__summary-value agent-mode__resolved-count">{decisions.length}</div>
								<div className="afs-review__summary-label">Auto-Resolved</div>
							</div>
						)}
						<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", filter === "review" && "afs-review__summary-item--active")} onClick={() => this.update({ filter: filter === "review" ? null : "review" })}>
							<div className="afs-review__summary-value afs-review__summary-value--warn">{remaining.length}</div>
							<div className="afs-review__summary-label">Need Review</div>
						</div>
						<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", filter === "passed" && "afs-review__summary-item--active")} onClick={() => this.update({ filter: filter === "passed" ? null : "passed" })}>
							<div className="afs-review__summary-value afs-review__summary-value--pass">{passedCount}</div>
							<div className="afs-review__summary-label">Passed</div>
						</div>
						<div className={clsx("afs-review__summary-item", "afs-review__summary-item--clickable", filter === null && "afs-review__summary-item--active")} onClick={() => this.update({ filter: null })}>
							<div className="afs-review__summary-value">{passedCount}/{totalCount}</div>
							<div className="afs-review__summary-label">Complete</div>
						</div>
					</div>

					{/* ── Auto-resolved ─────────────────────────────────────── */}
					{decisions.length > 0 && (filter === null || filter === "resolved") && (
						<div className="agent-mode__section">
							<div className="agent-mode__section-title">Auto-Resolved</div>
							{decisions.map((d, i) => {
								let hasEvidence = d.evidence && d.evidence.length > 0;
								let isConclusion = function (step) { return /^(conclusion|decision|recommended fix|recommended action|most likely cause)$/i.test(step || ""); };
								// Surface the headline reason above the fold
								let conclusionStep = hasEvidence ? d.evidence.find(function (e) { return isConclusion(e.step); }) : null;
								// Show ALL evidence steps in the investigation panel (including the conclusion).
								// Previously we filtered the conclusion out — but when the only step in evidence was
								// the conclusion, the investigation panel disappeared entirely. Better to keep the
								// full chain visible so the practitioner sees the agent's complete reasoning.
								let investigationSteps = hasEvidence ? d.evidence : [];
								let isOpen = showInvestigation[i] === undefined ? true : !!showInvestigation[i];

								return (
									<div key={i} className="agent-mode__resolved-card">
										{/* Main: action + reason */}
										<div className="agent-mode__resolved-main">
											<div className="agent-mode__resolved-icon">
												<Icon.Check size={13} />
											</div>
											<div className="agent-mode__resolved-content">
												<div className="agent-mode__resolved-action">{d.detail}</div>
												{conclusionStep && (
													<div className="agent-mode__resolved-reason">{conclusionStep.result}</div>
												)}
											</div>
											<span className="agent-mode__resolved-check">{d.check_id}</span>
										</div>

										{/* Investigation toggle */}
										{investigationSteps.length > 0 && (
											<button className="agent-mode__investigation-toggle" onClick={() => this.setState(function (prev) { let n = Object.assign({}, prev.showInvestigation); n[i] = !n[i]; return { showInvestigation: n }; })}>
												<Icon.ChevronRight size={12} className={clsx("agent-mode__investigation-chevron", isOpen && "agent-mode__investigation-chevron--open")} />
												{isOpen ? "Hide investigation" : "Show investigation"}
											</button>
										)}

										{/* Investigation detail */}
										{isOpen && investigationSteps.length > 0 && (() => {
											// Collect actionable transactions from any step (typically "Suspense breakdown")
											// and render them as a full-width block below the steps so the picker
											// has room to breathe instead of squeezing into the value column.
											let actionableTxs = [];
											for (let e of investigationSteps) {
												if (e.actionable_transactions && e.actionable_transactions.length > 0) {
													actionableTxs = actionableTxs.concat(e.actionable_transactions);
												}
											}
											return (
												<div className="agent-mode__investigation">
													{investigationSteps.map(function (e, j) {
														return (
															<div key={j} className="agent-mode__investigation-step">
																<span className="agent-mode__investigation-step-label">{e.step}</span>
																<span className="agent-mode__investigation-step-value">{e.result}</span>
															</div>
														);
													})}
													{actionableTxs.length > 0 && (
														<ActionableTransactionList
															transactions={actionableTxs}
															chartOfAccounts={chartOfAccounts}
															onReclassify={onApproveReclassification}
														/>
													)}
												</div>
											);
										})()}

									</div>
								);
							})}
						</div>
					)}

					{/* ── Needs review ──────────────────────────────────────── */}
					{remaining.length > 0 && (filter === null || filter === "review") && (
						<div className="agent-mode__section">
							<div className="agent-mode__section-title">Needs Review</div>
							<div>
								{remaining.map(function (r, i) {
									return (
										<div key={i} className="afs-review__item afs-review__item--fail">
											<Icon.XCircle size={18} color="#c62828" className="afs-review__icon" />
											<div className="afs-review__content">
												<p className="afs-review__label">{r.label}</p>
												{r.detail && <p className="afs-review__detail afs-review__detail--fail">{r.detail}</p>}
												<AgentCheckDrilldown
													r={r}
													decisions={decisions}
													onNavigateToDocumentation={onNavigateToDocumentation}
													onNavigateToReview={onNavigateToReview}
													onNavigateToClientRequests={onNavigateToClientRequests}
												/>
											</div>
											<span className="afs-review__check-section">{r.section}{r.id ? " (" + r.id + ")" : ""}</span>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* ── Passed checks (only when filtered) ────────────────── */}
					{filter === "passed" && (
						<div className="agent-mode__section">
							<div className="agent-mode__section-title">Passed</div>
							<div>
								{finalResults.filter(function (r) { return r.passed; }).map((r, i) => {
									let checkDecisions = decisions.filter(function (d) { return d.check_id === r.id; });
									let hasSteps = Array.isArray(r.execution_step_statuses) && r.execution_step_statuses.some(function (s) { return s.status !== "not_applicable"; });
									let hasSupporting = r.supporting && Object.keys(r.supporting).length > 0;
									let resultDocs = Array.isArray(r.documents) ? r.documents : [];
									let hasDrilldown = checkDecisions.length > 0 || hasSteps || hasSupporting || resultDocs.length > 0 || r.passed_reason || r.passed_label;
									let isOpen = !!expandedPassed[i];

									return (
										<div
											key={i}
											className="afs-review__item afs-review__item--pass"
											onClick={hasDrilldown ? () => this.setState(function (prev) { let n = Object.assign({}, prev.expandedPassed); n[i] = !n[i]; return { expandedPassed: n }; }) : undefined}
											style={hasDrilldown ? { cursor: "pointer" } : undefined}
										>
											<Icon.CheckCircle size={18} color="#2e7d32" className="afs-review__icon" />
											<div className="afs-review__content">
												<p className="afs-review__label">{r.label || r.id}</p>
												{isOpen && hasDrilldown && (
													<AgentCheckDrilldown
														r={r}
														decisions={decisions}
														onNavigateToDocumentation={onNavigateToDocumentation}
														onNavigateToReview={onNavigateToReview}
														onNavigateToClientRequests={onNavigateToClientRequests}
													/>
												)}
											</div>
											<span className="afs-review__check-section">{r.section || ""}{r.id ? " (" + r.id + ")" : ""}</span>
											{hasDrilldown && (
												<Icon.ChevronRight size={14} className={clsx("agent-mode__investigation-chevron", "agent-mode__investigation-chevron--meta", isOpen && "agent-mode__investigation-chevron--open")} />
											)}
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* ── Draft AFS ─────────────────────────────────────────── */}
					{manifest.draft_afs && (
						<div className="agent-mode__section">
							<div className="agent-mode__section-title">Draft AFS</div>
							<div className="agent-mode__review-card">
								<div className="agent-mode__review-item">
									<Icon.FileText size={14} color="#3b82f6" />
									<span className="agent-mode__review-label">Balance Sheet, Income Statement, and Notice to Reader generated</span>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Actions */}
				<div className="agent-mode__actions">
					{remaining.length > 0 && (
						<button className="btn btn-primary" onClick={() => { if (onNavigateToReview) onNavigateToReview(); }}>
							<Icon.ArrowRight size={14} className="agent-mode__btn-icon" />
							Review Remaining Checks
						</button>
					)}
					<button className="btn btn-secondary" onClick={this.startAgentMode}>
						<Icon.RefreshCw size={14} className="agent-mode__btn-icon" />
						Re-run
					</button>
					<button className="btn btn-secondary agent-mode__clear-btn" onClick={() => this.update({ showClearModal: true })}>
						<Icon.Trash2 size={14} className="agent-mode__btn-icon" />
						Clear Findings
					</button>
				</div>

				<Modal open={showClearModal} center onClose={() => { if (!clearing) this.update({ showClearModal: false }); }}>
					<div className="modal-title">Clear Agent Mode Findings?</div>
					<div className="modal-content">
						This will delete all agent-created journal entries and decisions for this engagement. The underlying transactions remain unchanged. This action cannot be undone.
					</div>
					<div className="modal-footer">
						<Button view="secondary" text="Cancel" onClick={() => this.update({ showClearModal: false })} disabled={clearing} />
						<Button text={clearing ? "Clearing..." : "Clear Findings"} onClick={this.confirmClearAgentMode} disabled={clearing} />
					</div>
				</Modal>
			</div>
		);
	}
}

export default AgentModeView;
